import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Switch,
  Textarea,
  Typography,
} from "@mui/joy";
import {
  ResponsiveTable,
  type ResponsiveColumn,
} from "../../components/common/ResponsiveTable";
import { apiClient } from "../../lib/api";
import { useContactSources } from "../../hooks/useCrm";

type Tier = {
  id: number;
  rank: string;
  name: string;
  description: string;
  base_price_per_square: string;
  waste_factor: string;
  pitch_multiplier_low: string;
  pitch_multiplier_med: string;
  pitch_multiplier_steep: string;
};
type Settings = {
  headline: string;
  disclaimer_text: string;
  lead_timing_mode: "before_price" | "after_price";
  show_price_range: boolean;
  price_range_pct: string;
  allowed_embed_domains: string[];
  notify_emails: string[];
  follow_organization_branding: boolean;
  brand_primary_color: string;
  brand_secondary_color: string;
  brand_logo_url: string;
  create_contact: boolean;
  contact_source: number | null;
  require_captcha: boolean;
  submissions_per_ip_per_hour: number;
  privacy_url: string;
};
type Widget = Settings & {
  id: number;
  embed_id: string;
  is_active: boolean;
  captcha_configured: boolean;
  tiers: Tier[];
  branding: { primary: string; secondary: string; logo_url: string };
};
type Draft = Omit<Settings, "allowed_embed_domains" | "notify_emails"> & {
  domains: string;
  notifications: string;
};
type Submission = {
  id: number;
  created_at: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  selected_tier_name: string;
  measurement_source: string;
  roof_area_sqft: string;
  avg_pitch_degrees: string | null;
  computed_price_low: string;
  computed_price_high: string;
  contact: number | null;
  homeowner_email_status: string;
  contractor_email_status: string;
  pricing_breakdown: Record<string, string>;
};
type Simulation = {
  tier_id: number;
  name: string;
  low: string;
  high: string;
  breakdown: Record<string, string>;
};
const base = "/api/marketing/roof-quote-widgets/";
function unwrap<T>(r: { data: T | null; status: number }): T {
  if (r.data == null) throw new Error(`Request failed (HTTP ${r.status})`);
  return r.data;
}
function errorText(e: unknown): string {
  const value = e as { responseData?: unknown; message?: string };
  return value.responseData
    ? JSON.stringify(value.responseData)
    : value.message || "Please try again.";
}
function money(v: string | number) {
  return Number(v).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}
function draftFor(w: Widget): Draft {
  return {
    headline: w.headline,
    disclaimer_text: w.disclaimer_text,
    lead_timing_mode: w.lead_timing_mode,
    show_price_range: w.show_price_range,
    price_range_pct: w.price_range_pct,
    follow_organization_branding: w.follow_organization_branding,
    brand_primary_color: w.brand_primary_color,
    brand_secondary_color: w.brand_secondary_color,
    brand_logo_url: w.brand_logo_url,
    create_contact: w.create_contact,
    contact_source: w.contact_source,
    require_captcha: w.require_captcha,
    submissions_per_ip_per_hour: w.submissions_per_ip_per_hour,
    privacy_url: w.privacy_url,
    domains: w.allowed_embed_domains.join("\n"),
    notifications: w.notify_emails.join("\n"),
  };
}
const splitLines = (value: string) =>
  value
    .split(/[\n,]+/)
    .map((v) => v.trim())
    .filter(Boolean);
const emailStatus = (value: string) =>
  ({
    sent: "Accepted by email provider",
    failed: "Sending failed",
    pending: "Pending",
    skipped: "No recipients configured",
    unknown: "Not recorded",
  })[value] || "Not recorded";

export function RoofQuoteWidgetPage() {
  const [widget, setWidget] = useState<Widget | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingTiers, setSavingTiers] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [area, setArea] = useState("2400");
  const [pitch, setPitch] = useState("26");
  const [simulation, setSimulation] = useState<Simulation[]>([]);
  const [simulationError, setSimulationError] = useState("");
  const [simulating, setSimulating] = useState(false);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<Submission | null>(null);
  const [exporting, setExporting] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [mobile, setMobile] = useState(false);
  const iframe = useRef<HTMLIFrameElement>(null);
  const sources = useContactSources();
  const initial = useQuery({
    queryKey: ["roof-quote-widget"],
    queryFn: async () =>
      unwrap(await apiClient.post<Widget>(`${base}mine/`, {})),
  });
  useEffect(() => {
    if (initial.data && !widget) {
      setWidget(initial.data);
      setDraft(draftFor(initial.data));
      setTiers(initial.data.tiers);
    }
  }, [initial.data, widget]);
  const filters = new URLSearchParams({
    search,
    date_from: dateFrom,
    date_to: dateTo,
    tier: tierFilter,
  });
  const filterString = filters.toString();
  const leads = useQuery({
    queryKey: ["roof-quote-leads", filterString, page],
    queryFn: async () =>
      unwrap(
        await apiClient.get<{
          count: number;
          next: string | null;
          results: Submission[];
        }>(
          `/api/marketing/roof-quote-submissions/?${filterString}&page=${page}`,
        ),
      ),
  });
  const range = draft?.price_range_pct;
  useEffect(() => {
    if (!widget) return;
    let current = true;
    setSimulating(true);
    const timer = window.setTimeout(async () => {
      try {
        const result = unwrap(
          await apiClient.post<{ results: Simulation[] }>(
            `${base}${widget.id}/simulate/`,
            {
              roof_area_sqft: area,
              avg_pitch_degrees: pitch,
              price_range_pct: range,
              tiers,
            },
          ),
        );
        if (current) {
          setSimulation(result.results);
          setSimulationError("");
        }
      } catch (e) {
        if (current) {
          setSimulation([]);
          setSimulationError(errorText(e));
        }
      } finally {
        if (current) setSimulating(false);
      }
    }, 300);
    return () => {
      current = false;
      window.clearTimeout(timer);
    };
  }, [widget, area, pitch, range, tiers]);
  useEffect(() => {
    if (!preview || !widget) return;
    const receive = async (event: MessageEvent) => {
      if (
        event.source !== iframe.current?.contentWindow ||
        event.data?.type !== "apexos-roof-quote:preview-price"
      )
        return;
      const { requestId, body } = event.data;
      const target = iframe.current.contentWindow;
      try {
        const result = unwrap(
          await apiClient.post<{ results: Simulation[] }>(
            `${base}${widget.id}/simulate/`,
            {
              roof_area_sqft: body.roof_area_sqft,
              avg_pitch_degrees: body.avg_pitch_degrees ?? 26,
            },
          ),
        );
        const price = result.results.find(
          (row) => row.tier_id === Number(body.tier_id),
        );
        if (!price) throw new Error("Choose a material to continue.");
        target?.postMessage(
          {
            type: "apexos-roof-quote:preview-result",
            requestId,
            result: {
              price: { low: price.low, high: price.high, currency: "USD" },
              message:
                "Test complete. No contacts, quotes, or emails were created.",
            },
          },
          "*",
        );
      } catch (e) {
        target?.postMessage(
          {
            type: "apexos-roof-quote:preview-result",
            requestId,
            error: errorText(e),
          },
          "*",
        );
      }
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [preview, widget]);
  async function saveSettings() {
    if (!widget || !draft) return;
    const snapshot = draft;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const { domains, notifications, ...values } = snapshot;
      const updated = unwrap(
        await apiClient.patch<Widget>(`${base}${widget.id}/`, {
          ...values,
          allowed_embed_domains: splitLines(domains),
          notify_emails: splitLines(notifications),
        }),
      );
      setWidget(updated);
      setDraft((current) =>
        JSON.stringify(current) === JSON.stringify(snapshot)
          ? draftFor(updated)
          : current,
      );
      setMessage("Settings saved.");
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  }
  async function saveTiers() {
    if (!widget) return;
    const snapshot = tiers;
    setSavingTiers(true);
    setError("");
    setMessage("");
    try {
      const updated = unwrap(
        await apiClient.patch<Widget>(`${base}${widget.id}/tiers/`, {
          tiers: snapshot,
        }),
      );
      setWidget(updated);
      setTiers((current) =>
        JSON.stringify(current) === JSON.stringify(snapshot)
          ? updated.tiers
          : current,
      );
      setMessage("All pricing tiers saved.");
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSavingTiers(false);
    }
  }
  async function toggleActive() {
    if (!widget) return;
    setToggling(true);
    setError("");
    try {
      setWidget(
        unwrap(
          await apiClient.patch<Widget>(`${base}${widget.id}/`, {
            is_active: !widget.is_active,
          }),
        ),
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setToggling(false);
    }
  }
  async function openPreview() {
    if (!widget) return;
    setPreviewLoading(true);
    setError("");
    try {
      setPreview(
        unwrap(
          await apiClient.get<{ html: string }>(`${base}${widget.id}/preview/`),
        ).html,
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setPreviewLoading(false);
    }
  }
  async function exportLeads() {
    setExporting(true);
    setError("");
    try {
      const csv = unwrap(
        await apiClient.get<string>(
          `/api/marketing/roof-quote-submissions/export/?${filterString}`,
        ),
      );
      const url = URL.createObjectURL(
        new Blob([csv], { type: "text/csv;charset=utf-8;" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = "roof-quote-leads.csv";
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setExporting(false);
    }
  }
  if (!widget || !draft)
    return (
      <Box sx={{ p: 3 }}>
        {initial.isError ? (
          <Alert
            color="danger"
            endDecorator={
              <Button onClick={() => initial.refetch()}>Retry</Button>
            }
          >
            {errorText(initial.error)}
          </Alert>
        ) : (
          <CircularProgress aria-label="Loading quote widget" />
        )}
      </Box>
    );
  const patch = (value: Partial<Draft>) =>
    setDraft((current) => current && { ...current, ...value });
  const field = (label: string, key: keyof Draft, type = "text") => (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Input
        type={type}
        value={String(draft[key] ?? "")}
        onChange={(e) =>
          patch({
            [key]:
              key === "submissions_per_ip_per_hour"
                ? Number(e.target.value)
                : e.target.value,
          })
        }
      />
    </FormControl>
  );
  const toggle = (
    label: string,
    key:
      | "show_price_range"
      | "follow_organization_branding"
      | "create_contact"
      | "require_captcha",
  ) => (
    <FormControl
      orientation="horizontal"
      sx={{ justifyContent: "space-between", gap: 2 }}
    >
      <FormLabel>{label}</FormLabel>
      <Switch
        checked={draft[key]}
        onChange={(e) => patch({ [key]: e.target.checked })}
        disabled={
          key === "require_captcha" &&
          !widget.captcha_configured &&
          !draft.require_captcha
        }
        slotProps={{ input: { "aria-label": label } }}
      />
    </FormControl>
  );
  const snippet = `<script src="${window.location.origin}/widget/roof-quote/loader.js" data-embed-id="${widget.embed_id}" defer></script>`;
  const settingsDirty =
    JSON.stringify(draft) !== JSON.stringify(draftFor(widget));
  const tiersDirty = JSON.stringify(tiers) !== JSON.stringify(widget.tiers);
  const columns: ResponsiveColumn<Submission>[] = [
    {
      key: "name",
      header: "Homeowner",
      mobileVariant: "header",
      render: (row) => (
        <Button variant="plain" onClick={() => setDetail(row)}>
          {row.first_name} {row.last_name}
        </Button>
      ),
    },
    {
      key: "address",
      header: "Property",
      mobileVariant: "body",
      render: (row) => (
        <Box>
          <Typography level="body-sm">{row.address}</Typography>
          <Typography level="body-xs">{row.email}</Typography>
        </Box>
      ),
    },
    {
      key: "tier",
      header: "Material / estimate",
      render: (row) => (
        <Box>
          <Typography level="body-sm">{row.selected_tier_name}</Typography>
          <Typography level="body-xs">
            {money(row.computed_price_low)} – {money(row.computed_price_high)}
          </Typography>
        </Box>
      ),
    },
    {
      key: "created_at",
      header: "Received",
      render: (row) => new Date(row.created_at).toLocaleDateString(),
    },
    {
      key: "contact",
      header: "Follow up",
      render: (row) =>
        row.contact ? (
          <Button
            component="a"
            href={`/crm/contacts/${row.contact}`}
            size="sm"
            variant="soft"
          >
            Open CRM
          </Button>
        ) : (
          <Typography level="body-xs">No linked contact</Typography>
        ),
    },
  ];
  return (
    <Stack
      spacing={3}
      sx={{ p: { xs: 2, md: 3 }, maxWidth: 1440, mx: "auto", minWidth: 0 }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        gap={2}
      >
        <Box>
          <Typography level="h2">Roof Quote Widget</Typography>
          <Typography level="body-md">
            Turn roof estimates into conversations with homeowners.
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" gap={1}>
          <Chip color={widget.is_active ? "success" : "neutral"}>
            {widget.is_active ? "Active" : "Paused"}
          </Chip>
          <Button variant="outlined" loading={toggling} onClick={toggleActive}>
            {widget.is_active ? "Pause widget" : "Activate widget"}
          </Button>
        </Stack>
      </Stack>
      {error && <Alert color="danger">{error}</Alert>}
      {message && <Alert color="success">{message}</Alert>}
      <Card>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          gap={2}
        >
          <Box>
            <Typography level="title-lg">Installation & test</Typography>
            <Typography level="body-sm">
              Preview saved settings on desktop or mobile. Test submissions
              never create leads or send email.
            </Typography>
          </Box>
          <Button onClick={openPreview} loading={previewLoading}>
            Preview & test
          </Button>
        </Stack>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {[
            [widget.is_active, "Widget active"],
            [
              widget.tiers.length === 3 &&
                widget.tiers.every((t) => Number(t.base_price_per_square) > 0),
              "Three priced tiers",
            ],
            [widget.notify_emails.length > 0, "Notifications configured"],
            [widget.allowed_embed_domains.length > 0, "Embed domains set"],
            [
              !widget.require_captcha || widget.captcha_configured,
              "Verification ready",
            ],
          ].map(([ok, label]) => (
            <Chip
              key={String(label)}
              color={ok ? "success" : "warning"}
              variant="soft"
            >
              {ok ? "✓" : "○"} {label}
            </Chip>
          ))}
        </Stack>
        <Textarea
          readOnly
          value={snippet}
          minRows={2}
          slotProps={{ textarea: { "aria-label": "Embed code" } }}
        />
        <Button
          variant="outlined"
          sx={{ alignSelf: "start" }}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(snippet);
              setMessage("Embed code copied.");
            } catch {
              setError(
                "Could not copy automatically. Select and copy the embed code above.",
              );
            }
          }}
        >
          Copy embed code
        </Button>
      </Card>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 1.3fr) minmax(0, 1fr)",
          },
          gap: 3,
          "& > *": { minWidth: 0 },
        }}
      >
        <Card>
          <Typography level="title-lg">Experience & branding</Typography>
          {field("Headline", "headline")}
          <FormControl>
            <FormLabel>Estimate disclaimer</FormLabel>
            <Textarea
              value={draft.disclaimer_text}
              onChange={(e) => patch({ disclaimer_text: e.target.value })}
              minRows={2}
            />
          </FormControl>
          <FormControl>
            <FormLabel>When to collect contact details</FormLabel>
            <Select
              value={draft.lead_timing_mode}
              onChange={(_, value) =>
                value && patch({ lead_timing_mode: value })
              }
            >
              <Option value="after_price">
                Show estimate first, then offer follow-up
              </Option>
              <Option value="before_price">
                Collect contact details before estimate
              </Option>
            </Select>
          </FormControl>
          {toggle("Show a price range", "show_price_range")}
          {field("Range half-width (0.10 = ±10%)", "price_range_pct", "number")}
          {toggle(
            "Follow organization branding",
            "follow_organization_branding",
          )}
          {draft.follow_organization_branding ? (
            <Typography level="body-sm">
              Uses your organization’s current colors and logo automatically.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {field("Primary color", "brand_primary_color", "color")}
              {field("Secondary color", "brand_secondary_color", "color")}
              {field("Custom logo URL", "brand_logo_url", "url")}
            </Stack>
          )}
          {field("Privacy policy URL", "privacy_url", "url")}
        </Card>
        <Card>
          <Typography level="title-lg">Lead routing & protection</Typography>
          {toggle("Create CRM contacts", "create_contact")}
          <FormControl>
            <FormLabel>CRM lead source</FormLabel>
            <Select
              value={
                draft.contact_source == null ? "" : String(draft.contact_source)
              }
              onChange={(_, value) =>
                patch({ contact_source: value === "" ? null : Number(value) })
              }
            >
              <Option value="">No source</Option>
              {(sources.data || []).map((source) => (
                <Option key={source.id} value={String(source.id)}>
                  {source.name}
                </Option>
              ))}
            </Select>
            {sources.isError && (
              <FormHelperText>
                Lead sources could not load. Existing selection is preserved.
              </FormHelperText>
            )}
          </FormControl>
          <FormControl>
            <FormLabel>Notification emails</FormLabel>
            <Textarea
              value={draft.notifications}
              onChange={(e) => patch({ notifications: e.target.value })}
              minRows={2}
            />
            <FormHelperText>
              One address per line or separated by commas.
            </FormHelperText>
          </FormControl>
          <FormControl>
            <FormLabel>Allowed embed domains</FormLabel>
            <Textarea
              value={draft.domains}
              onChange={(e) => patch({ domains: e.target.value })}
              minRows={2}
              placeholder="yourroofingcompany.com"
            />
            <FormHelperText>
              Hostnames only; subdomains are included. An empty list permits any
              site.
            </FormHelperText>
          </FormControl>
          {field(
            "Submissions per IP per hour (1–100)",
            "submissions_per_ip_per_hour",
            "number",
          )}
          {toggle("Require CAPTCHA verification", "require_captcha")}
          {!widget.captcha_configured && (
            <Typography level="body-sm">
              CAPTCHA becomes available when your deployment administrator
              configures Turnstile keys.
            </Typography>
          )}
          <Box sx={{ mt: "auto" }}>
            <Button
              loading={saving}
              disabled={!settingsDirty}
              onClick={saveSettings}
            >
              Save settings
            </Button>
            {settingsDirty && (
              <Typography level="body-xs" sx={{ mt: 1 }}>
                Unsaved settings
              </Typography>
            )}
          </Box>
        </Card>
      </Box>
      <Card>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          gap={2}
        >
          <Box>
            <Typography level="title-lg">Material pricing</Typography>
            <Typography level="body-sm">
              All three tiers save together. Prices are per roofing square (100
              sq ft).
            </Typography>
          </Box>
          <Button
            onClick={saveTiers}
            loading={savingTiers}
            disabled={!tiersDirty}
          >
            Save all tiers
          </Button>
        </Stack>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: 2,
          }}
        >
          {tiers.map((tier) => (
            <Card variant="soft" key={tier.id}>
              <Chip sx={{ alignSelf: "start", textTransform: "capitalize" }}>
                {tier.rank}
              </Chip>
              {(
                [
                  ["Material name", "name"],
                  ["Description", "description"],
                  ["Price per square ($)", "base_price_per_square"],
                  ["Waste factor (0–1)", "waste_factor"],
                  ["Low pitch multiplier", "pitch_multiplier_low"],
                  ["Medium pitch multiplier", "pitch_multiplier_med"],
                  ["Steep pitch multiplier", "pitch_multiplier_steep"],
                ] as const
              ).map(([label, key]) => (
                <FormControl key={key}>
                  <FormLabel>{label}</FormLabel>
                  <Input
                    slotProps={{
                      input: { "aria-label": `${tier.rank} ${label}` },
                    }}
                    type={
                      key === "name" || key === "description"
                        ? "text"
                        : "number"
                    }
                    value={tier[key]}
                    onChange={(e) =>
                      setTiers((current) =>
                        current.map((row) =>
                          row.id === tier.id
                            ? { ...row, [key]: e.target.value }
                            : row,
                        ),
                      )
                    }
                  />
                </FormControl>
              ))}
            </Card>
          ))}
        </Box>
      </Card>
      <Card>
        <Typography level="title-lg">Live pricing simulator</Typography>
        <Typography level="body-sm">
          Uses your draft pricing and range. Nothing is saved, and no leads or
          emails are created.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
          <FormControl>
            <FormLabel>Sample roof area (sq ft)</FormLabel>
            <Input
              type="number"
              value={area}
              onChange={(e) => setArea(e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Sample pitch (degrees)</FormLabel>
            <Input
              type="number"
              value={pitch}
              onChange={(e) => setPitch(e.target.value)}
            />
          </FormControl>
        </Stack>
        {simulationError && <Alert color="warning">{simulationError}</Alert>}
        {simulating && (
          <Typography level="body-sm">Updating estimates…</Typography>
        )}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: 2,
            opacity: simulating ? 0.5 : 1,
          }}
        >
          {simulation.map((row) => (
            <Card key={row.tier_id} variant="soft">
              <Typography level="title-md">{row.name}</Typography>
              <Typography level="h3">
                {draft.show_price_range
                  ? `${money(row.low)} – ${money(row.high)}`
                  : money((Number(row.low) + Number(row.high)) / 2)}
              </Typography>
              <Typography level="body-sm">
                {row.breakdown.squares_raw} squares ×{" "}
                {1 + Number(row.breakdown.waste_factor)} waste allowance ×{" "}
                {money(row.breakdown.base_price_per_square)} ×{" "}
                {row.breakdown.pitch_multiplier} pitch
              </Typography>
              <Typography level="body-xs">
                Base estimate {money(row.breakdown.base_price)} · range ±
                {Number(row.breakdown.range_pct) * 100}%
              </Typography>
            </Card>
          ))}
        </Box>
      </Card>
      <Card>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          gap={2}
        >
          <Box>
            <Typography level="title-lg">Quote leads</Typography>
            <Typography level="body-sm">
              {leads.data?.count ?? "—"} matching submissions
            </Typography>
          </Box>
          <Button variant="outlined" onClick={exportLeads} loading={exporting}>
            Export all matching leads
          </Button>
        </Stack>
        <CollapsibleFilters><Stack direction={{ xs: "column", md: "row" }} gap={1}>
          <Input
            placeholder="Search name, email, or address"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            sx={{ flex: 1 }}
            slotProps={{ input: { "aria-label": "Search quote leads" } }}
          />
          <FormControl>
            <FormLabel>From</FormLabel>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Through</FormLabel>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
            />
          </FormControl>
          <Select
            value={tierFilter}
            onChange={(_, value) => {
              setTierFilter(value || "");
              setPage(1);
            }}
            aria-label="Filter by material"
          >
            <Option value="">All materials</Option>
            {widget.tiers.map((tier) => (
              <Option key={tier.id} value={String(tier.id)}>
                {tier.name}
              </Option>
            ))}
          </Select>
        </Stack></CollapsibleFilters>
        {leads.isError ? (
          <Alert
            color="danger"
            endDecorator={
              <Button onClick={() => leads.refetch()}>Retry</Button>
            }
          >
            Could not load leads. {errorText(leads.error)}
          </Alert>
        ) : leads.isPending ? (
          <CircularProgress />
        ) : (
          <ResponsiveTable
            rows={leads.data?.results || []}
            columns={columns}
            getRowKey={(row) => row.id}
            emptyState="No quote leads match these filters."
          />
        )}
        <Stack direction="row" justifyContent="space-between">
          <Button
            variant="plain"
            disabled={page === 1 || leads.isFetching}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <Typography level="body-sm">Page {page}</Typography>
          <Button
            variant="plain"
            disabled={!leads.data?.next || leads.isFetching}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </Stack>
      </Card>
      <Drawer
        open={!!detail}
        onClose={() => setDetail(null)}
        anchor="right"
        size="md"
      >
        <ModalClose />
        <Box sx={{ p: 3, pt: 6, overflowY: "auto" }}>
          {detail && (
            <Stack gap={2}>
              <Typography level="h3">
                {detail.first_name} {detail.last_name}
              </Typography>
              <Typography>{detail.address}</Typography>
              <Typography>
                {detail.email} · {detail.phone || "No phone provided"}
              </Typography>
              <Divider />
              <Typography level="title-lg">
                {detail.selected_tier_name}
              </Typography>
              <Typography>
                {money(detail.computed_price_low)} –{" "}
                {money(detail.computed_price_high)}
              </Typography>
              <Typography>
                {detail.roof_area_sqft} sq ft ·{" "}
                {detail.avg_pitch_degrees ?? "Unknown"}° pitch ·{" "}
                {{
                  solar_api: "Google Solar",
                  footprint: "Building footprint",
                  manual_draw: "Manual measurement",
                }[detail.measurement_source] || detail.measurement_source}
              </Typography>
              <Typography level="body-sm">
                Submitted {new Date(detail.created_at).toLocaleString()}
              </Typography>
              {detail.pricing_breakdown.base_price && (
                <Typography level="body-sm">
                  Base estimate {money(detail.pricing_breakdown.base_price)} ·
                  Waste {Number(detail.pricing_breakdown.waste_factor) * 100}% ·
                  Pitch multiplier {detail.pricing_breakdown.pitch_multiplier}
                </Typography>
              )}
              <Typography level="title-md">Email status</Typography>
              <Typography level="body-sm">
                Homeowner: {emailStatus(detail.homeowner_email_status)}
              </Typography>
              <Typography level="body-sm">
                Contractor: {emailStatus(detail.contractor_email_status)}
              </Typography>
              <Typography level="body-xs">
                Provider acceptance does not confirm inbox delivery.
              </Typography>
              {detail.contact && (
                <Button component="a" href={`/crm/contacts/${detail.contact}`}>
                  Open CRM contact
                </Button>
              )}
            </Stack>
          )}
        </Box>
      </Drawer>
      <Modal open={preview !== null} onClose={() => setPreview(null)}>
        <ModalDialog
          sx={{
            width: "min(1050px, 96vw)",
            maxHeight: "94vh",
            overflow: "auto",
          }}
        >
          <ModalClose />
          <Typography level="h3">Widget preview & test</Typography>
          <Typography level="body-sm">
            Saved settings · Test data only · No leads or email
          </Typography>
          <Stack direction="row" gap={1}>
            <Button
              variant={mobile ? "outlined" : "solid"}
              onClick={() => setMobile(false)}
            >
              Desktop
            </Button>
            <Button
              variant={mobile ? "solid" : "outlined"}
              onClick={() => setMobile(true)}
            >
              Mobile
            </Button>
          </Stack>
          <Box
            sx={{
              width: mobile ? 390 : "100%",
              maxWidth: "100%",
              mx: "auto",
              minHeight: 520,
            }}
          >
            <iframe
              ref={iframe}
              title="Roof quote test preview"
              sandbox="allow-scripts"
              srcDoc={preview || ""}
              style={{
                width: "100%",
                height: "58vh",
                minHeight: 480,
                border: 0,
              }}
            />
          </Box>
        </ModalDialog>
      </Modal>
    </Stack>
  );
}
